import CmsBaseClass from "@/helpers/cms/CmsBaseClass.js";
import CmsLink from "@/helpers/cms/CmsLink.js";
import CmsMediaQuery from "@/helpers/editor/blocks/CmsMediaQuery.js";
import Vue from "vue";
import {tr} from "../../../vue-components/helpers/i18n.js";
import SeoHelper from "../../../vue-components/helpers/SeoHelper.js";
import CompatibilityHelper from "@/helpers/CompatibilityHelper.js";

export default class CmsPage extends CmsBaseClass {
    static TYPES = {
        MAP: `map`,
        EDITABLE_PAGE: `blocks`,
        HTML: `html`,
        PAGE: `page`,
        TEMPLATE: `template`,
        LANDING: `landing`,
        SEARCH: `search`,
        CMS_ITEM_LIST: `cms-item-list`,
        CMS_ITEM_VIEW: `cms-item-view`
    }

    static BACKGROUNDS = {
        NONE: `none`,
        IMAGE: `image`,
        COLOR: `color`
        // GRADIENT: `gradient`
    }

    get ignoredFields() {
        return [`cms`, `itemTypeDef`, `item`, `itemList`, `mediaProxy`];
    }

    get autoGenerateId() {
        return false;
    }

    get displayName() {
        return tr(this.name);
    }

    constructor(data, cms, route) {
        super(data);

        // Properties
        this.item = null;
        this.itemList = [];
        this.cms = cms;

        if (this.category === undefined) {
            this.category = ``;
        }

        if (this.itemType) {
            this.itemTypeDef = cms.getTypeById(this.itemType);
        }

        if (this.mediaQuery && this.mediaQuery.expanded === undefined) {
            this.mediaQuery = null;
        }

        // object to hold mobile, kiosk and tablet variants
        this.mediaQuery = new CmsMediaQuery(this.mediaQuery);

        // The Media Proxy will use the component values or the one from mediaQuery with the device and preview
        this.mediaProxy = this.mediaQuery.createProxy(this);

        this.fallbackLink = new CmsLink(this.fallbackLink);

        if (!data) {
            this.type = CmsPage.TYPES.EDITABLE_PAGE;
            this.dealerid = null;

            // used for navigation
            this.name = {
                en: `New Page`,
                fr: `Nouvelle page`
            };
            this.glyph = null;
            this.template = `none`;
            this.homePage = false; // note that home page get "/" permalink
            this.permalink = {
                en: `new-page`,
                fr: `nouvelle-page`
            };
            this.blogSettings = {};

            this.category = ``;

            this.itemType = ``;
            this.editable = true;

            // any css
            this.css = ``;
            this.customClass = ``;

            this.websites = [];
            this.blocks = [];

            this.background = CmsPage.BACKGROUNDS.NONE;
            this.backgroundColor = `#ffffff`;
            this.backgroundImage = null;

            this.navigationBehavior = `visible`;
        } else {
            this.backgroundColor = CompatibilityHelper.checkColor(this.backgroundColor);

            this.blocks = [];

            if (data.blocks) {
                for (let block of data.blocks) {
                    this.blocks.push(window.cmsFactory.new(block, this));
                }
            }
        }

        if (this.type === `cms-item-view`) {
            if (route.name === `cms-page-renderer`) {
                this.item = cms.getItemByPermalink(cms.getRouteId(route.fullPath));
            } else {
                this.item = cms.getFirstItemByType(this.itemType);
            }
        }

        this.footer = this.mergeDefaults(this.footer, {
            active: true
        });

        this.header = this.mergeDefaults(this.header, {
            active: true
        });

        this.ai = this.mergeDefaults(this.ai, {
            keywords: {},
            specs: {},
            meta: {},
            review: {},
            enhance: {},
            rewrite: {},
            suggestions: {},
            translation: {}
        });

        if (this.pageSpecs) {
            this.specs = this.pageSpecs;
            delete this.pageSpecs;
        }

        // all these attributes are localized
        this.specs = this.mergeDefaults(this.specs, {
            words: {},
            titles: {},
            paragraphs: {},
            images: {},
            // keywords specifications include min and max + variations (plural, semantic)
            mainKeywords: {},
            keywords: {}
        });

        // all these attributes are localized
        this.hiddenMeta = this.mergeDefaults(this.hiddenMeta, {
            topic: {}, // google search intent that should bring to this page
            audience: {},
            targetLocation: {en: `USA`, fr: `Québec`},
            targetMarket: {},
            // arrays of keywords for building the page
            mainKeywords: {}, // max 2 here
            keywords: {},
            briefDocUrl: ``,
            competitors: {}// contains arrays of competitors pages
        });

        // compatibility tmp
        if (this.reviewData) {
            this.ai.review.en = this.reviewData;
            delete this.reviewData;
        }

        // SEO
        this.meta = this.mergeDefaults(this.meta,
            {
                title: {},
                description: {},
                keywords: [], // used as headers meta
                searchVisibility: `visible`,
                socialImage: null,
                socialTitle: {
                    sameAsSEO: true
                },
                socialDescription: {
                    sameAsSEO: true
                }
            }
        );
        this.syncSocial();

        // Compatibility
        if (this.isVersionBefore(`1.0.0`)) {
            console.warn(`Page below 1.0.0 version, refreshing component ID to make them unique`);
            this.applyNewId();
            this.editorVersion = `0.9.0`;
        }
    }

    toJSON() {
        let data = super.toJSON();
        data.editorVersion = window.env.VUE_APP_PACKAGE_VERSION;
        return data;
    }

    syncSocial() {
        if (this.meta.socialTitle.sameAsSEO) {
            for (let lang in this.meta.title) {
                this.meta.socialTitle[lang] = this.meta.title[lang];
            }
        }
        if (this.meta.socialDescription.sameAsSEO) {
            for (let lang in this.meta.description) {
                this.meta.socialDescription[lang] = this.meta.description[lang];
            }
        }
    }

    applyNewId() {
        // never change page id, it's a MongoDB ObjectID
        for (let block of this.blocks) {
            block.applyNewId();
        }
    }

    isVersionBefore(version) {
        return !this.editorVersion || this.editorVersion < version;
    }

    addBlock(block) {
        this.blocks.push(block);
        return block;
    }

    insertBlock(block, index) {
        this.blocks.splice(index + 1, 0, block);
        return block;
    }

    duplicateBlock(block) {
        const copy = block.duplicate();
        this.insertBlock(copy, this.blocks.indexOf(block));
        return copy;
    }

    deleteBlock(block) {
        this.blocks.removeItem(block);
    }

    swapBlock(block, delta) {
        let index = this.blocks.indexOf(block);
        let newIndex = index + delta;
        if (newIndex >= 0 && newIndex < this.blocks.length) {
            let swaped = this.blocks[index];
            Vue.set(this.blocks, index, this.blocks[newIndex]);
            Vue.set(this.blocks, newIndex, swaped);
            // this.blocks[newIndex] = swaped;
        } else {
            console.error(`Invalid index: Impossible to move block`);
        }
    }

    getComponents(componentType = `all`, includeChildren = false, contentOnly = true) {
        let components = [];
        for (let block of this.blocks) {
            if (block.isHiddenAll)
                continue;

            components = components.concat(block.getComponents(componentType, contentOnly, includeChildren));
        }
        return components;
    }

    findComponent(id) {
        for (let block of this.blocks) {
            if (block.isHiddenAll)
                continue;

            const component = block.findComponent(id);
            if (component)
                return component;
        }
        return null;
    }

    async translate(from, to, replaceAll = false, context = null) {
        for (let block of this.blocks) {
            await block.translate(from, to, replaceAll, context);
        }
    }

    async resetLanguageTexts(language) {
        for (let block of this.blocks) {
            await block.resetLanguageTexts(language);
        }
    }

    getMeta() {
        return SeoHelper.getMeta(tr(this.meta.title) || tr(this.name), tr(this.meta.description), tr(this.meta.keywords), `Eye-In Media`, this.meta.socialImage);
    }

    getKeywordStats(language) {
        let stats = {
            min: 0,
            max: 0,
            keywordLength: 0,
            avgWords: 0,
            minDensity: 0,
            maxDensity: 0
        }
        for (let keyword of this.specs.keywords[language]) {
            stats.min += keyword.min;
            stats.max += keyword.max;
            stats.keywordLength += keyword.keyword.split(/\s+/).length;
            stats.avgWords += stats.keywordLength * (keyword.max + keyword.min) / 2;
        }

        if (this.specs.words[language]) {
            stats.minDensity = Math.round(stats.avgWords / this.specs.words[language].min);
            stats.maxDensity = Math.round(stats.avgWords / this.specs.words[language].max);
        }

        return stats;
    }
}
